import { html } from "htm/preact";
import { useState } from "preact/hooks";
import { navigate } from "../router.js";
import { getKnownTournaments, type Role } from "../storage.js";
import { getIdentity } from "../identity.js";
import { InstallHint } from "../components/InstallHint.js";
import { Mark } from "../components/Brand.js";
import { Icon } from "../components/Icon.js";
import { TERMS } from "../copy.js";

const ROLE_LABEL: Record<Role, string> = {
  admin: TERMS.Organizer,
  player: "Player",
  spectator: "Watching",
};

/** The stored role is what this device *is*; an organiser who left the app playing
 * should still read as playing here, or the list contradicts the app bar. */
function roleLabel(role: Role, code: string): string {
  if (role === "admin" && !getIdentity(code)?.canOrganize) return `Playing · ${TERMS.Organizer}`;
  return ROLE_LABEL[role];
}

/** Where reopening a game from this list should land you, which is the screen that
 * role actually uses: the organiser has setup to do, a spectator has the clubhouse.
 *
 * An organiser who left the app playing is sent where a player goes, and not to a
 * screen their own switch has put away. */
function homeFor(role: Role, code: string): string {
  if (role === "admin" && getIdentity(code)?.canOrganize) return `/t/${code}/setup`;
  if (role === "spectator") return `/t/${code}/clubhouse`;
  return `/t/${code}/leaderboard`;
}

export function Home() {
  const [code, setCode] = useState("");
  const known = getKnownTournaments();

  function goJoin(e: Event) {
    e.preventDefault();
    if (code.length === 4) navigate(`/join/${code.toUpperCase()}`);
  }

  return html`
    <section class="hero">
      <${Mark} size=${52} class="hero-mark" />
      <h1>Settle it on the course.</h1>
      <p>Live scoring for your group, so you can save your excuses for the swing.</p>
    </section>

    <${InstallHint} />

    <form class="stack" onSubmit=${goJoin}>
      <label class="field">
        <span class="field-label">${`Have a ${TERMS.event} code?`}</span>
        <input
          class="code-input"
          value=${code}
          maxlength="4"
          placeholder="ABCD"
          autocapitalize="characters"
          autocomplete="off"
          spellcheck="false"
          aria-label=${`Four-letter ${TERMS.event} code`}
          onInput=${(e: any) => setCode(e.currentTarget.value.toUpperCase())}
        />
      </label>
      <button type="submit" disabled=${code.length !== 4}>
        <${Icon} name="check" /> Join
      </button>
    </form>

    <div class="action-grid">
      <button class="action-card" onClick=${() => navigate("/create")}>
        <${Icon} name="flag" size=${22} />
        ${`New ${TERMS.event}`}
        <small>Set up rounds, players and formats</small>
      </button>
      <button class="action-card" onClick=${() => navigate("/library")}>
        <${Icon} name="book" size=${22} />
        Courses
        <small>Browse the shared course list</small>
      </button>
    </div>

    ${known.length > 0 &&
    html`
      <h2>${TERMS.Events}</h2>
      <div class="tap-list">
        ${known.map(
          (t) => html`
            <button
              key=${t.code}
              onClick=${() => navigate(homeFor(t.role, t.code))}
            >
              <span class="avatar">${(t.name || t.code).trim().charAt(0).toUpperCase()}</span>
              <span class="list-main">
                ${t.name}
                <small class="list-sub">${roleLabel(t.role, t.code)}</small>
              </span>
              <span class="chip chip-code">${t.code}</span>
              <${Icon} name="right" size=${18} />
            </button>
          `,
        )}
      </div>
    `}

    <!-- The rescue path lives down here rather than beside the code box, and quietly.
         The guard on it is possession of the token — the screen verifies that before it
         writes anything, and #/t/<code>/organizer/<token> has always been an unlisted
         route anyone could hit — so being discreet buys no safety. What it buys is a
         front page that stays about playing golf, and the person who needs this is
         looking for it. Don't promote it. -->
    <div class="footer-links">
      <button onClick=${() => navigate("/restore")}>
        ${known.length === 0 ? `Have an ${TERMS.organizer} link?` : "Restore access"}
      </button>
      <button onClick=${() => navigate("/admin")}>Admin & usage</button>
    </div>
  `;
}
